import { Link } from "react-router-dom";
import { MapPin, Mail, Clock, Facebook, Instagram, Twitter, Youtube, Linkedin, Globe, ChevronRight, ShieldCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { resolveImageUrl } from "@/lib/api";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, x: Twitter, youtube: Youtube, linkedin: Linkedin };

export const Footer = () => {
  const { content } = useSite();
  const footer = content.footer || {};
  const theme = content.theme || {};
  const siteTitle = content.seo?.site_title || "SSH Auto Sales";
  const copyright = (footer.copyright || "").replace("{year}", new Date().getFullYear());
  const mapsUrl = footer.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(footer.address)}` : null;

  return (
    <footer className="bg-[#0D0F12] text-zinc-400" data-testid="site-footer">
      {/* Tier 2: Main grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-12 gap-x-8 gap-y-10">
          <div className="col-span-12 lg:col-span-4">
            <div className="mb-4">
              {theme.logo_url ? (
                <img src={resolveImageUrl(theme.logo_url)} alt={siteTitle} className="h-8 w-auto object-contain" />
              ) : (
                <span className="font-display text-xl font-black text-white">
                  SSH <span className="text-primary">AUTO SALES</span>
                </span>
              )}
            </div>
            {footer.description && (
              <p className="text-sm text-zinc-400 leading-relaxed max-w-xs" data-testid="footer-description">{footer.description}</p>
            )}
            <div className="inline-flex items-center gap-2 mt-5 px-3 py-1.5 border border-primary/30 bg-primary/10 text-primary text-xs font-mono uppercase tracking-[0.15em]">
              <ShieldCheck className="h-3.5 w-3.5" /> Licensed Alberta Dealer
            </div>
            {footer.social?.length > 0 && (
              <div className="flex gap-3 mt-5">
                {footer.social.map((s) => {
                  const Icon = SOCIAL_ICONS[(s.label || "").toLowerCase()] || Globe;
                  return (
                    <a
                      key={s.id || s.url}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="p-2 border border-[#232833] text-zinc-400 hover:text-primary hover:border-primary hover:bg-primary/10 transition-colors"
                      data-testid={`footer-social-${(s.label || "link").toLowerCase()}`}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {footer.links?.length > 0 && (
            <div className="col-span-6 sm:col-span-3 lg:col-span-2">
              <h4 className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-white mb-5">
                <span className="h-2 w-2 bg-primary" /> Quick Links
              </h4>
              <ul className="space-y-2.5 text-sm">
                {[...footer.links, { id: "f-sold", label: "Recently Sold", url: "/inventory/sold" }].map((l) => (
                  <li key={l.id || l.url}>
                    <Link to={l.url} className="group inline-flex items-center gap-1 hover:text-primary transition-colors" data-testid={`footer-link-${(l.id || l.label || "").toLowerCase().replace(/\s/g, "-")}`}>
                      <ChevronRight className="h-3 w-3 text-primary opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(footer.address || footer.email || footer.hours) && (
            <div className="col-span-6 sm:col-span-4 lg:col-span-3">
              <h4 className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-white mb-5">
                <span className="h-2 w-2 bg-primary" /> Visit Us
              </h4>
              <ul className="space-y-3.5 text-sm">
                {footer.address && (
                  <li className="flex items-start gap-2.5">
                    <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{footer.address}</a>
                  </li>
                )}
                {footer.email && (
                  <li className="flex items-center gap-2.5">
                    <Mail className="h-4 w-4 text-primary shrink-0" />
                    <a href={`mailto:${footer.email}`} className="hover:text-primary transition-colors break-all" data-testid="footer-email-link">{footer.email}</a>
                  </li>
                )}
                {footer.hours && (
                  <li className="flex items-start gap-2.5">
                    <Clock className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {footer.hours}
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="col-span-12 sm:col-span-5 lg:col-span-3">
            <div className="border border-[#232833] bg-[#15181E] p-5 h-full">
              <h4 className="font-display text-base font-black uppercase tracking-tight text-white mb-2">Looking for a truck or SUV?</h4>
              <p className="text-xs text-zinc-400 leading-relaxed mb-4">Trade-ins appraised daily. Competitive Alberta financing on approved credit.</p>
              <Link to="/financing" className="btn-themed w-full text-center text-xs" data-testid="footer-financing-cta">
                Estimate Payments
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Tier 3: Legal bar */}
      <div className="border-t border-[#232833]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-zinc-500">
          {copyright && <span data-testid="footer-copyright">{copyright}</span>}
          <span className="font-mono uppercase tracking-[0.2em]">Edmonton · Alberta · Canada</span>
        </div>
      </div>
    </footer>
  );
};
